import React, { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { WORDS } from 'routes/routeConstant'
import type { OnboardingLesson, OnboardingSeriesMeta } from '../../../redux/actions/onboardingVideosAction'
import TextbookCallout from './TextbookCallout'
import './onboarding-video-series.css'

interface Props {
  series: OnboardingSeriesMeta
  lessons: OnboardingLesson[]
  /** Lesson currently being marked (shows a working state on its button). */
  markingLessonId: string | null
  /** Per-lesson error message, keyed by lessonId. */
  markErrorById: Record<string, string>
  /** Lesson marked watched in this session — held on screen to confirm. */
  justWatchedId: string | null
  onMarkWatched: (lessonId: string) => void
  /** Dismisses the confirmation and moves the card on to the next lesson. */
  onContinue: () => void
  onDismiss: () => void
}

/** Characters shown in the collapsed preview strip before the full table opens. */
const PREVIEW_CHARS = 8

const PlayIcon: React.FC = () => (
  <svg viewBox='0 0 24 24' width='22' height='22' aria-hidden='true'>
    <path d='M8 5.5v13l11-6.5-11-6.5z' fill='currentColor' />
  </svg>
)

const CheckIcon: React.FC = () => (
  <svg viewBox='0 0 24 24' width='14' height='14' aria-hidden='true'>
    <path d='M9.6 16.2 4.8 11.4l1.4-1.4 3.4 3.4 8-8 1.4 1.4z' fill='currentColor' />
  </svg>
)

const ChevronIcon: React.FC = () => (
  <svg viewBox='0 0 24 24' width='12' height='12' aria-hidden='true'>
    <path d='m6 9 6 6 6-6' fill='none' stroke='currentColor' strokeWidth='2.4' strokeLinecap='round' />
  </svg>
)

/** First English sense only — dictionary entries pack several senses after a slash/comma. */
const primarySense = (english: string): string => {
  if (!english) return ''
  const first = english.split('/')[0].split(',')[0].trim()
  return first.length > 28 ? `${first.slice(0, 27)}…` : first
}

const YouTubePoster: React.FC<{ youtubeId: string; title: string }> = ({ youtubeId, title }) => {
  const { t } = useTranslation('landing')
  const [playing, setPlaying] = useState(false)
  const [thumb, setThumb] = useState(`https://i.ytimg.com/vi/${youtubeId}/maxresdefault.jpg`)

  if (playing) {
    return (
      <div className='obv-video'>
        <iframe
          className='obv-video__iframe'
          src={`https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1&rel=0&modestbranding=1`}
          title={title}
          allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
          allowFullScreen
        />
      </div>
    )
  }

  return (
    <button
      type='button'
      className='obv-video obv-video--poster'
      onClick={() => setPlaying(true)}
      aria-label={t('videoSeries.playVideo', { title })}
    >
      <img
        className='obv-video__img'
        src={thumb}
        alt=''
        loading='lazy'
        onError={() => setThumb(`https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`)}
      />
      <span className='obv-video__scrim' />
      <span className='obv-video__play'>
        <PlayIcon />
      </span>
    </button>
  )
}

/** CSS state class for one dot of the progress track. */
const dotState = (lesson: OnboardingLesson, currentId: string | null): string =>
  `${lesson.watched ? 'is-done' : ''} ${lesson.id === currentId ? 'is-current' : ''}`.trim() || 'is-todo'

/**
 * The series at a glance: one dot per lesson, filled as they're watched. It
 * replaces the old stack of completed rows and the row of upcoming chips —
 * same information, one line instead of several hundred pixels.
 */
const ProgressTrack: React.FC<{ lessons: OnboardingLesson[]; currentId: string | null; label: string }> = ({
  lessons,
  currentId,
  label,
}) => (
  <p className='obv-track'>
    <span className='obv-track__dots' aria-hidden='true'>
      {lessons.map((l) => (
        <span key={l.id} className={`obv-track__dot ${dotState(l, currentId)}`} />
      ))}
    </span>
    <span className='obv-track__label'>{label}</span>
  </p>
)

/** The current lesson: video on the left, everything about it on the right. */
const LessonBlock: React.FC<{
  lesson: OnboardingLesson
  isMarking: boolean
  error?: string
  /** Shown once the words are saved, to move on to the next lesson. */
  continueLabel: string | null
  onMarkWatched: (lessonId: string) => void
  onContinue: () => void
}> = ({ lesson, isMarking, error, continueLabel, onMarkWatched, onContinue }) => {
  const { t } = useTranslation('landing')
  const [expanded, setExpanded] = useState(false)
  const added = lesson.watched
  const hasWords = lesson.words.length > 0
  const preview = lesson.words.slice(0, PREVIEW_CHARS)
  const hiddenCount = lesson.words.length - preview.length
  const vocId = `obv-voc-${lesson.id}`

  return (
    <div className='obv-lesson'>
      <div className='obv-lesson__grid'>
        {lesson.youtubeId && <YouTubePoster youtubeId={lesson.youtubeId} title={lesson.title} />}

        <div className='obv-panel'>
          <p className='obv-panel__tag'>{t('videoSeries.lessonTag', { order: lesson.order })}</p>
          <h3 className='obv-panel__title'>{lesson.title}</h3>
          {lesson.subtitle && <p className='obv-panel__sub'>{lesson.subtitle}</p>}

          {hasWords && (
            <div className={`obv-chars ${added ? 'is-added' : ''}`}>
              <p className='obv-chars__head'>
                {added && (
                  <span className='obv-chars__check'>
                    <CheckIcon />
                  </span>
                )}
                {added
                  ? t('videoSeries.addedToLearning')
                  : t('videoSeries.newCharacters', { count: lesson.wordCount })}
              </p>
              <span className='obv-chars__strip'>
                {preview.map((w) => (
                  <span key={w.word} className='obv-chars__chip' lang='zh'>
                    {w.word}
                  </span>
                ))}
                {hiddenCount > 0 && (
                  <button
                    type='button'
                    className='obv-chars__chip obv-chars__chip--more'
                    onClick={() => setExpanded((v) => !v)}
                    aria-expanded={expanded}
                    aria-controls={vocId}
                    aria-label={t('videoSeries.showAllCharacters', { count: lesson.wordCount })}
                  >
                    +{hiddenCount}
                  </button>
                )}
              </span>
              <button
                type='button'
                className={`obv-chars__toggle ${expanded ? 'is-open' : ''}`}
                onClick={() => setExpanded((v) => !v)}
                aria-expanded={expanded}
                aria-controls={vocId}
              >
                {expanded ? t('videoSeries.hideMeanings') : t('videoSeries.showMeanings')}
                <ChevronIcon />
              </button>
            </div>
          )}

          <div className='obv-panel__foot'>
            {added ? (
              <div className='obv-saved'>
                <p className='obv-done'>
                  <span className='obv-done__check'>
                    <CheckIcon />
                  </span>
                  <span>
                    <Trans
                      t={t}
                      i18nKey='videoSeries.reviewInMyWords'
                      components={{ myWords: <Link to={WORDS} className='obv-link' /> }}
                    />
                  </span>
                </p>
                {continueLabel && (
                  <button type='button' className='obv-mark' onClick={onContinue}>
                    {continueLabel}
                  </button>
                )}
              </div>
            ) : (
              <button type='button' className='obv-mark' onClick={() => onMarkWatched(lesson.id)} disabled={isMarking}>
                {isMarking ? t('videoSeries.saving') : t('videoSeries.markWatched', { count: lesson.wordCount })}
              </button>
            )}
            {error && <p className='obv-err'>{error}</p>}
          </div>
        </div>
      </div>

      {hasWords && expanded && (
        <div id={vocId} className={`obv-voc ${added ? 'is-added' : ''}`}>
          {lesson.words.map((w) => (
            <div key={w.word} className='obv-voc__cell'>
              <span className='obv-voc__han' lang='zh'>
                {w.word}
              </span>
              <span className='obv-voc__py'>{w.pinyin}</span>
              <span className='obv-voc__en'>{primarySense(w.english)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const OnboardingVideoSeriesView: React.FC<Props> = ({
  series,
  lessons,
  markingLessonId,
  markErrorById,
  justWatchedId,
  onMarkWatched,
  onContinue,
  onDismiss,
}) => {
  const { t } = useTranslation('landing')
  const byOrder = (a: OnboardingLesson, b: OnboardingLesson) => a.order - b.order
  const ordered = [...lessons].sort(byOrder)
  const available = ordered.filter((l) => l.status === 'available')

  // One lesson at a time: the first unwatched lesson is the whole card, the
  // rest of the series lives in the progress track. A lesson just marked
  // watched holds the card until the learner moves on, so saving the words is
  // actually confirmed rather than swapping the lesson out from under them.
  const upNext = available.find((l) => !l.watched) ?? null
  const justWatched = justWatchedId ? (available.find((l) => l.id === justWatchedId && l.watched) ?? null) : null
  const current = justWatched ?? upNext
  const completedCount = available.filter((l) => l.watched).length
  const next = current ? (ordered.find((l) => l.order > current.order) ?? null) : null

  // Only offered when there is somewhere to go: the next lesson is published.
  const continueLabel = justWatched && upNext ? t('videoSeries.startLesson', { order: upNext.order }) : null

  const trackLabel = current
    ? t('videoSeries.trackCurrent', { order: current.order, total: ordered.length })
    : t('videoSeries.trackAllDone', { count: completedCount })

  // The series introduces itself once, on lesson 1; after that it stays out of
  // the way and the lesson card speaks for itself.
  const isFirstVisit = current?.order === 1

  return (
    <section className='obv' aria-labelledby='obv-title'>
      <div className='obv__top'>
        <div className='obv__heading'>
          <p className='obv__kicker'>
            <span className='obv__kicker-rule' />
            {t('videoSeries.kicker', { level: series.level })}
          </p>
          <h2 id='obv-title' className='obv__title'>
            {series.title}
          </h2>
        </div>

        <div className='obv__aside'>
          <ProgressTrack lessons={ordered} currentId={current?.id ?? null} label={trackLabel} />
          <button type='button' className='obv__close' onClick={onDismiss}>
            {t('videoSeries.notNow')}
          </button>
        </div>
      </div>

      {isFirstVisit && <p className='obv__lead'>{series.description}</p>}

      {current && (
        <LessonBlock
          key={current.id}
          lesson={current}
          isMarking={markingLessonId === current.id}
          error={markErrorById[current.id]}
          continueLabel={continueLabel}
          onMarkWatched={onMarkWatched}
          onContinue={onContinue}
        />
      )}

      {!current && (
        <p className='obv-allclear'>
          {next ? (
            <Trans
              t={t}
              i18nKey='videoSeries.allClearMore'
              components={{ myWords: <Link to={WORDS} className='obv-link' /> }}
            />
          ) : (
            <Trans
              t={t}
              i18nKey='videoSeries.allClearDone'
              components={{ myWords: <Link to={WORDS} className='obv-link' /> }}
            />
          )}
        </p>
      )}

      <div className='obv__foot'>
        {next && (
          <p className='obv-next'>
            <span className='obv-next__tag'>{t('videoSeries.upNext')}</span>
            <span className='obv-next__title'>
              {next.title
                ? t('videoSeries.nextLessonWithTitle', { order: next.order, title: next.title })
                : t('videoSeries.nextLesson', { order: next.order })}
            </span>
            {next.status !== 'available' && <span className='obv-next__soon'>{t('videoSeries.comingSoon')}</span>}
          </p>
        )}
        <TextbookCallout
          textbook={series.textbook}
          targetWordCount={series.targetWordCount}
          variant={isFirstVisit ? 'full' : 'compact'}
        />
      </div>
    </section>
  )
}

export default OnboardingVideoSeriesView
